import type { DashboardPeriodLabel } from "../../types/dashboard";

export type PeriodWord =
  | "today"
  | "thisWeek"
  | "thisMonth"
  | "thisYear"
  | "custom"
  | "all";

const PERIOD_WORD_BY_KEY: Record<DashboardPeriodLabel, PeriodWord> = {
  today: "today",
  this_week: "thisWeek",
  this_month: "thisMonth",
  this_year: "thisYear",
  custom: "custom",
  all: "all",
};

export function periodWord(period: DashboardPeriodLabel): PeriodWord {
  return PERIOD_WORD_BY_KEY[period] ?? "all";
}

export function getCardTitles(period: DashboardPeriodLabel): {
  revenue: string;
  tickets: string;
  failedPayments: string;
  cancelledBookings: string;
  refundsByAdmin: string;
  refundsByCustomer: string;
  topCinema: string;
} {
  switch (period) {
    case "today":
      return {
        revenue: "Today's Revenue",
        tickets: "Today's Tickets Sold",
        failedPayments: "Failed Payments (Today)",
        cancelledBookings: "Cancelled Bookings (Today)",
        refundsByAdmin: "Refunds by Admin (Today)",
        refundsByCustomer: "Refunds by Customer (Today)",
        topCinema: "Top Cinema Today",
      };
    case "this_week":
      return {
        revenue: "This Week's Revenue",
        tickets: "This Week's Tickets Sold",
        failedPayments: "Failed Payments (Week)",
        cancelledBookings: "Cancelled Bookings (Week)",
        refundsByAdmin: "Refunds by Admin (Week)",
        refundsByCustomer: "Refunds by Customer (Week)",
        topCinema: "Top Cinema This Week",
      };
    case "this_month":
      return {
        revenue: "This Month's Revenue",
        tickets: "This Month's Tickets Sold",
        failedPayments: "Failed Payments (Month)",
        cancelledBookings: "Cancelled Bookings (Month)",
        refundsByAdmin: "Refunds by Admin (Month)",
        refundsByCustomer: "Refunds by Customer (Month)",
        topCinema: "Top Cinema This Month",
      };
    case "this_year":
      return {
        revenue: "Yearly Revenue",
        tickets: "Yearly Tickets Sold",
        failedPayments: "Failed Payments (Year)",
        cancelledBookings: "Cancelled Bookings (Year)",
        refundsByAdmin: "Refunds by Admin (Year)",
        refundsByCustomer: "Refunds by Customer (Year)",
        topCinema: "Top Cinema This Year",
      };
    case "custom":
      return {
        revenue: "Custom Revenue",
        tickets: "Custom Tickets Sold",
        failedPayments: "Failed Payments (Custom)",
        cancelledBookings: "Cancelled Bookings (Custom)",
        refundsByAdmin: "Refunds by Admin (Custom)",
        refundsByCustomer: "Refunds by Customer (Custom)",
        topCinema: "Top Cinema (Custom)",
      };
    case "all":
    default:
      return {
        revenue: "Total Revenue",
        tickets: "Total Tickets Sold",
        failedPayments: "Failed Payments (All)",
        cancelledBookings: "Cancelled Bookings (All)",
        refundsByAdmin: "Refunds by Admin (All)",
        refundsByCustomer: "Refunds by Customer (All)",
        topCinema: "Top Cinema (All)",
      };
  }
}

export function getComparatorLabel(period: DashboardPeriodLabel): string {
  switch (period) {
    case "today":
      return "vs yesterday";
    case "this_week":
      return "vs last week";
    case "this_month":
      return "vs last month";
    case "this_year":
      return "vs last year";
    case "custom":
      return "vs previous period";
    case "all":
    default:
      return "all time";
  }
}

export function getChartTitle(
  period: DashboardPeriodLabel,
  cinemaName: string | null,
): string {
  const periodPart =
    period === "all"
      ? "All Time"
      : period === "today"
        ? "Today"
        : period === "this_week"
          ? "This Week"
          : period === "this_month"
            ? "This Month"
            : period === "this_year"
              ? "This Year"
              : "Custom Range";
  const cinemaPart = cinemaName ? ` · ${cinemaName}` : " · All Cinemas";
  return `Revenue & Bookings Trend — ${periodPart}${cinemaPart}`;
}

export function getTopCinemaSubtext(
  period: DashboardPeriodLabel,
  cinemaName: string | null,
): string {
  if (cinemaName) return "selected cinema";
  switch (period) {
    case "today":
      return "today";
    case "this_week":
      return "this week";
    case "this_month":
      return "this month";
    case "this_year":
      return "this year";
    case "custom":
      return "custom range";
    case "all":
    default:
      return "all time";
  }
}

export function getQuickStatsLifetimeNote(): string {
  return "All time";
}

export function formatPercent(value: number): string {
  if (!Number.isFinite(value)) return "0%";
  return `${Math.abs(value).toFixed(1)}%`;
}

export function getAnalyticsPeriod(
  period: DashboardPeriodLabel,
): "today" | "7d" | "30d" | "365d" | "custom" {
  switch (period) {
    case "today":
      return "7d";
    case "this_week":
      return "7d";
    case "this_month":
      return "30d";
    case "this_year":
      return "365d";
    case "custom":
      return "custom";
    case "all":
    default:
      return "365d";
  }
}